import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import I18n from '/src/utils/translations'
import { MdMyLocation, MdSave, MdClose, MdMap, MdLocationOn } from 'react-icons/md'
import { useGPSLocation } from '/src/hooks/use_gps_location'
import GoogleMapPicker from '/src/ui/core/maps/google_map'
import '/src/static/css/gps.css'
import { Window } from '@progress/kendo-react-dialogs'

export default function GPSModal({ show, onClose, onSave, initialValue }) {
  const {
    data,
    getCurrentLocation,
    handleClear,
    handleMapLocationSelect,
    getMapCenter,
    updateData,
    canSave,
    hasCoordinates
  } = useGPSLocation(initialValue, show)

  const handleSave = useCallback(() => {
    const lat = parseFloat(data.latitude)
    const lng = parseFloat(data.longitude)
    const isValidLat = !Number.isNaN(lat) && lat >= -90 && lat <= 90
    const isValidLng = !Number.isNaN(lng) && lng >= -180 && lng <= 180

    if (!isValidLat || !isValidLng) {
      updateData({ error: I18n.t('form.inputs.gps.invalid_coordinates') })
      return
    }

    const gpsValue = JSON.stringify([lat, lng])
    onSave(gpsValue)
    onClose()
  }, [data.latitude, data.longitude, updateData, onSave, onClose])

  const handleClose = useCallback(() => {
    updateData({ showMap: false, error: '' })
    onClose()
  }, [updateData, onClose])

  const toggleMap = useCallback(() => {
    updateData({ showMap: !data.showMap })
  }, [updateData, data.showMap])

  const modalStyle = {
    height: data.showMap ? 650 : 420,
    width: data.showMap ? 800 : 650,
    minHeight: 350,
    minWidth: 600
  }

  return (
    show && (
      <Window
        title={I18n.t('form.inputs.gps.modal_title')}
        onClose={handleClose}
        modal
        draggable={false}
        height={modalStyle.height}
        width={modalStyle.width}
        sizeInPx={true}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        }}
        className="input-gps-wrapper"
        loading={{ status: data.isLoading, text: I18n.t('form.inputs.gps.loading') }}
        closable={true}
        minimizeButton={() => null}
        maximizeButton={() => null}
        restoreButton={() => null}
        applyOverlay={true}
        appendToBody={false}
      >
        <div className="gps-modal-content">
          {/* Controls Section */}
          <div className="gps-controls-section">
            <div className="gps-action-buttons">
              <button 
                type="button" 
                className="action-button geolocation-btn" 
                onClick={getCurrentLocation}
                disabled={data.isLoading}
                title={I18n.t('form.inputs.gps.get_current_location')}
              >
                <MdMyLocation /> {I18n.t('form.inputs.gps.get_current_location')}
              </button>
              
              <button 
                type="button" 
                className="action-button toggle-map-btn" 
                onClick={toggleMap}
              >
                <MdMap /> {data.showMap ? I18n.t('form.inputs.gps.hide_map') : I18n.t('form.inputs.gps.show_map')}
              </button>
            </div>
          </div>

          {/* Map Section */}
          {data.showMap && !data.isLoading && (
            <div className="gps-map-section">
              <h4 className="map-instruction">
                {I18n.t('form.inputs.gps.click_to_mark')}
              </h4>
              <div className="gps-map-container">
                <GoogleMapPicker
                  center={getMapCenter()}
                  zoom={hasCoordinates ? 15 : 4}
                  onLocationSelect={handleMapLocationSelect}
                  height="250px"
                />
              </div>
            </div>
          )}

          {/* Coordinates Display */}
          {hasCoordinates && !data.isLoading && (
            <div className="gps-coordinates-preview">
              <div className="coordinate-header">
                <MdLocationOn className="location-icon" />
                <span className="coordinate-label">
                  {I18n.t('form.inputs.gps.coordinates')}
                </span>
              </div>
              <div className="coordinate-display">
                <div className="coordinate-main">
                  {parseFloat(data.latitude).toFixed(6)}, {parseFloat(data.longitude).toFixed(6)}
                </div>
                <div className="coordinate-details">
                  {I18n.t('form.inputs.gps.latitude')}: {data.latitude} |{" "}
                  {I18n.t('form.inputs.gps.longitude')}: {data.longitude}
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {data.isLoading && (
            <div className="gps-loading-state">
              <div className="loading-text">
                {I18n.t('form.inputs.gps.get_current_location')}
              </div>
            </div>
          )}

          {/* Error Display */}
          {data.error && (
            <div className="gps-error-message">
              <div className="error-text">{data.error}</div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="gps-modal-footer">
            <button 
              type="button" 
              className="action-button cancel-btn" 
              onClick={handleClose}
            >
              <MdClose /> {I18n.t('actions.cancel')}
            </button>
            
            <button 
              type="button" 
              className="action-button clear-btn" 
              onClick={handleClear}
              disabled={!hasCoordinates}
            >
              {I18n.t('actions.clear')}
            </button>
            
            <button 
              type="button" 
              className="action-button save-btn" 
              onClick={handleSave}
              disabled={!canSave}
            >
              <MdSave /> {I18n.t('actions.save')}
            </button>
          </div>
        </div>
      </Window>
    )
  )
}

GPSModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialValue: PropTypes.string
}

GPSModal.defaultProps = {
  initialValue: null
}